import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  AlertTitle,
  CircularProgress,
  Stack,
  Tabs,
  Tab,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Switch,
  FormControlLabel,
} from '@mui/material';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import GavelIcon from '@mui/icons-material/Gavel';
import { leaveBatch4Api } from './leaveBatch4Api';

const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '20px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 4px 20px rgba(17,24,39,0.04)',
};

export default function Batch4ReconciliationCompOffYearEndPanel() {
  const [subTab, setSubTab] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Batch 4 Feature Gates
  const [b4Config, setB4Config] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);

  // 1. Worked-on-Leave Reconciliation (TASK-LEV-009)
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [scanStartDate, setScanStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [scanEndDate, setScanEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [resolveDialog, setResolveDialog] = useState({
    open: false,
    review: null,
    decision: 'CONFIRMED_WORKED',
    credit_days: '1.00',
    reason: '',
  });

  // 2. Compensatory Off Grants & 60-Day Expiry (TASK-LEV-010)
  const [compOffs, setCompOffs] = useState([]);
  const [loadingCompOffs, setLoadingCompOffs] = useState(false);
  const [claimForm, setClaimForm] = useState({
    work_date: new Date().toISOString().slice(0, 10),
    hours_worked: '8.50',
    reason: '',
  });
  const [expiryAsOfDate, setExpiryAsOfDate] = useState(new Date().toISOString().slice(0, 10));
  const [expiryPreview, setExpiryPreview] = useState(null);

  // 3. Year-End Preview & Rollover (TASK-LEV-008)
  const [cycleYear, setCycleYear] = useState(new Date().getFullYear());
  const [yearEndPreview, setYearEndPreview] = useState(null);
  const [yearEndBatches, setYearEndBatches] = useState([]);
  const [loadingYearEnd, setLoadingYearEnd] = useState(false);

  const loadInitialData = useCallback(async () => {
    try {
      const cfg = await leaveBatch4Api.getBatch4Config();
      setB4Config(cfg);
    } catch (_err) {
      // Fail-closed fallback
    }
  }, []);

  const loadReviews = useCallback(async () => {
    try {
      setLoadingReviews(true);
      const data = await leaveBatch4Api.getAttendanceReviews();
      setReviews(data.results || []);
    } catch (_err) {
      setReviews([]);
    } finally {
      setLoadingReviews(false);
    }
  }, []);

  const loadCompOffs = useCallback(async () => {
    try {
      setLoadingCompOffs(true);
      const data = await leaveBatch4Api.getCompOffCredits();
      setCompOffs(data.results || []);
    } catch (_err) {
      setCompOffs([]);
    } finally {
      setLoadingCompOffs(false);
    }
  }, []);

  const loadYearEndBatches = useCallback(async () => {
    try {
      const data = await leaveBatch4Api.getYearEndBatches();
      setYearEndBatches(data.results || []);
    } catch (_err) {
      setYearEndBatches([]);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
    loadReviews();
    loadCompOffs();
    loadYearEndBatches();
  }, [loadInitialData, loadReviews, loadCompOffs, loadYearEndBatches]);

  const handleToggleConfig = async (field, value) => {
    try {
      setSavingConfig(true);
      setErrorMsg('');
      const updated = await leaveBatch4Api.updateBatch4Config({ ...b4Config, [field]: value });
      setB4Config(updated);
      setSuccessMsg('Batch 4 governance gate updated.');
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Failed to update Batch 4 feature gate.');
    } finally {
      setSavingConfig(false);
    }
  };

  // Worked-on-Leave Handlers
  const handleScanConflicts = async (dryRun = false) => {
    try {
      setErrorMsg('');
      setSuccessMsg('');
      const res = await leaveBatch4Api.scanAttendanceReviews({
        start_date: scanStartDate,
        end_date: scanEndDate,
        dry_run: dryRun,
      });
      setSuccessMsg(
        dryRun
          ? `Dry-run scan complete: ${res.detected_count} worked-on-leave conflict(s) detected.`
          : `Conflict scan complete: ${res.detected_count} candidate(s) found (${res.created_count} newly queued).`
      );
      if (!dryRun) {
        setReviews(res.reviews || []);
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Worked-on-leave conflict scan failed.');
    }
  };

  const handleConfirmResolve = async () => {
    if (!resolveDialog.review) return;
    try {
      setErrorMsg('');
      await leaveBatch4Api.resolveAttendanceReview(resolveDialog.review.id, {
        decision: resolveDialog.decision,
        credit_days: resolveDialog.credit_days,
        reason: resolveDialog.reason,
      });
      setSuccessMsg(`Review #${resolveDialog.review.id} resolved as ${resolveDialog.decision}.`);
      setResolveDialog({ open: false, review: null, decision: 'CONFIRMED_WORKED', credit_days: '1.00', reason: '' });
      loadReviews();
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Failed to resolve worked-on-leave review.');
    }
  };

  // Comp-Off Handlers
  const handleClaimCompOff = async () => {
    try {
      setErrorMsg('');
      await leaveBatch4Api.requestCompOffCredit(claimForm);
      setSuccessMsg('Compensatory Off claim submitted for approval (60-day validity from work date).');
      setClaimForm((prev) => ({ ...prev, reason: '' }));
      loadCompOffs();
    } catch (err) {
      const msg =
        err.response?.data?.work_date ||
        err.response?.data?.hours_worked ||
        err.response?.data?.detail ||
        'Failed to submit Comp-Off claim.';
      setErrorMsg(Array.isArray(msg) ? msg.join(' ') : String(msg));
    }
  };

  const handleApproveCompOff = async (id) => {
    try {
      setErrorMsg('');
      await leaveBatch4Api.approveCompOffCredit(id, 'Approved via HR Portal');
      setSuccessMsg(`Comp-Off credit #${id} approved and granted to ledger.`);
      loadCompOffs();
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Failed to approve Comp-Off credit.');
    }
  };

  const handleRejectCompOff = async (id) => {
    try {
      setErrorMsg('');
      await leaveBatch4Api.rejectCompOffCredit(id, 'Rejected via HR Portal');
      setSuccessMsg(`Comp-Off credit #${id} rejected.`);
      loadCompOffs();
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Failed to reject Comp-Off credit.');
    }
  };

  const handleSweepExpiry = async (dryRun) => {
    try {
      setErrorMsg('');
      const res = await leaveBatch4Api.sweepCompOffExpiry(expiryAsOfDate, dryRun);
      setExpiryPreview(res);
      setSuccessMsg(
        dryRun
          ? `Expiry dry-run complete: ${res.grants_evaluated} grant(s) eligible (${res.total_expired_days} days).`
          : `Expired ${res.grants_expired_count} Comp-Off grant(s) (${res.total_expired_days} days).`
      );
      if (!dryRun) {
        loadCompOffs();
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Comp-Off expiry sweep failed.');
    }
  };

  // Year-End Handlers
  const handlePreviewYearEnd = async () => {
    try {
      setLoadingYearEnd(true);
      setErrorMsg('');
      const res = await leaveBatch4Api.previewYearEnd(cycleYear);
      setYearEndPreview(res);
      setSuccessMsg(
        `Read-only Year-End ${cycleYear} preview generated: ${res.employees_evaluated} employee(s) evaluated (Zero DB writes).`
      );
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Failed to generate year-end preview.');
    } finally {
      setLoadingYearEnd(false);
    }
  };

  const handleExecuteYearEnd = async () => {
    try {
      setLoadingYearEnd(true);
      setErrorMsg('');
      await leaveBatch4Api.executeYearEnd(cycleYear);
      setSuccessMsg(`Year-End ${cycleYear} rollover batch executed.`);
      loadYearEndBatches();
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Year-end rollover execution is disabled or failed.');
    } finally {
      setLoadingYearEnd(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* DEC-001 & Fail-Closed Governance Notice */}
      <Alert severity="warning" icon={<GavelIcon fontSize="inherit" />} sx={{ borderRadius: '16px' }}>
        <AlertTitle sx={{ fontWeight: 800 }}>
          MOD-05 Batch 4 Governance & DEC-001 Non-Payroll Isolation
        </AlertTitle>
        All Batch 4 execution workflows default to <strong>Disabled (Fail-Closed)</strong>. Under{' '}
        <strong>DEC-001</strong>, year-end leave encashment remains strictly <strong>Preview-Only</strong> with zero
        mutations to payroll, payslips, tax, or Form 16.
      </Alert>

      {errorMsg && (
        <Alert severity="error" onClose={() => setErrorMsg('')} sx={{ borderRadius: '12px' }}>
          {errorMsg}
        </Alert>
      )}
      {successMsg && (
        <Alert severity="success" onClose={() => setSuccessMsg('')} sx={{ borderRadius: '12px' }}>
          {successMsg}
        </Alert>
      )}

      {/* Feature Gates Card */}
      {b4Config && (
        <Paper sx={CARD_SX}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5, color: '#1e293b' }}>
            Batch 4 Fail-Closed Execution Feature Gates
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} flexWrap="wrap">
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(b4Config.is_worked_on_leave_reconciliation_active)}
                  disabled={savingConfig}
                  onChange={(e) => handleToggleConfig('is_worked_on_leave_reconciliation_active', e.target.checked)}
                />
              }
              label="Worked-on-Leave Reconciliation Active"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(b4Config.is_comp_off_enabled)}
                  disabled={savingConfig}
                  onChange={(e) => handleToggleConfig('is_comp_off_enabled', e.target.checked)}
                />
              }
              label="Comp-Off Engine Active"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(b4Config.is_comp_off_allocation_sync_active)}
                  disabled={savingConfig}
                  onChange={(e) => handleToggleConfig('is_comp_off_allocation_sync_active', e.target.checked)}
                />
              }
              label="Comp-Off Ledger Allocation Sync Active"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(b4Config.is_year_end_processing_active)}
                  disabled={savingConfig}
                  onChange={(e) => handleToggleConfig('is_year_end_processing_active', e.target.checked)}
                />
              }
              label="Year-End Rollover Execution Active"
            />
          </Stack>
        </Paper>
      )}

      {/* Sub-Navigation Tabs */}
      <Paper sx={{ ...CARD_SX, p: 1 }}>
        <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<FactCheckIcon />} iconPosition="start" label="Worked-on-Leave Reconciliation" />
          <Tab icon={<AccessTimeIcon />} iconPosition="start" label="Compensatory Off & 60-Day Expiry" />
          <Tab icon={<EventRepeatIcon />} iconPosition="start" label="Year-End Preview & Rollover" />
        </Tabs>
      </Paper>

      {/* SUB-TAB 0: Worked-on-Leave Reconciliation */}
      {subTab === 0 && (
        <Paper sx={CARD_SX}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            Biometric Worked-on-Leave Review Queue
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 2.5 }}>
            Verified attendance punches on approved modern leave days never auto-cancel leaves. Review conflicts below
            to authorize audited 0.50d or 1.00d ledger credits.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }} alignItems="center">
            <TextField
              label="Scan Start Date"
              type="date"
              size="small"
              value={scanStartDate}
              onChange={(e) => setScanStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Scan End Date"
              type="date"
              size="small"
              value={scanEndDate}
              onChange={(e) => setScanEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button variant="outlined" onClick={() => handleScanConflicts(true)}>
              Dry-Run Conflict Scan
            </Button>
            <Button variant="contained" onClick={() => handleScanConflicts(false)}>
              Queue Conflicts for Review
            </Button>
          </Stack>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Conflict Date</TableCell>
                  <TableCell>Leave Type</TableCell>
                  <TableCell>Worked Hours</TableCell>
                  <TableCell>Recommended</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingReviews ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress size={22} />
                    </TableCell>
                  </TableRow>
                ) : reviews.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3, color: '#64748b' }}>
                      No worked-on-leave review candidates found.
                    </TableCell>
                  </TableRow>
                ) : (
                  reviews.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>{r.employee_name}</TableCell>
                      <TableCell>{r.conflict_date}</TableCell>
                      <TableCell>{r.leave_type_code}</TableCell>
                      <TableCell>{r.worked_hours}h</TableCell>
                      <TableCell>
                        {r.recommended_action} ({r.recommended_credit_days}d)
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={r.status}
                          color={r.status === 'CONFIRMED_WORKED' ? 'success' : r.status === 'PENDING_REVIEW' ? 'warning' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {r.status === 'PENDING_REVIEW' && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              setResolveDialog({
                                open: true,
                                review: r,
                                decision: 'CONFIRMED_WORKED',
                                credit_days: String(r.recommended_credit_days || '1.00'),
                                reason: '',
                              })
                            }
                          >
                            Resolve Conflict
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* SUB-TAB 1: Compensatory Off & 60-Day Expiry */}
      {subTab === 1 && (
        <Stack spacing={3}>
          <Paper sx={CARD_SX}>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
              Compensatory Off Grant & 60-Day Expiry Engine
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', mb: 2.5 }}>
              Eligibility: &ge; 4.00 hours worked on a Weekly Off or Gazetted Holiday = 0.50 day; &ge; 8.00 hours = 1.00
              day. Approved credits expire 60 calendar days after work_date.
            </Typography>

            <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Work Date"
                  value={claimForm.work_date}
                  onChange={(e) => setClaimForm({ ...claimForm, work_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Hours Worked"
                  value={claimForm.hours_worked}
                  onChange={(e) => setClaimForm({ ...claimForm, hours_worked: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Reason / Holiday Work Note"
                  value={claimForm.reason}
                  onChange={(e) => setClaimForm({ ...claimForm, reason: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Button fullWidth variant="contained" onClick={handleClaimCompOff}>
                  Submit Comp-Off Claim
                </Button>
              </Grid>
            </Grid>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Work Date</TableCell>
                    <TableCell>Day Type</TableCell>
                    <TableCell>Hours</TableCell>
                    <TableCell>Credited / Avail</TableCell>
                    <TableCell>Expiry Date (Work + 60d)</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingCompOffs ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <CircularProgress size={22} />
                      </TableCell>
                    </TableRow>
                  ) : compOffs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 3, color: '#64748b' }}>
                        No Comp-Off credits recorded.
                      </TableCell>
                    </TableRow>
                  ) : (
                    compOffs.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell>{c.employee_name}</TableCell>
                        <TableCell>{c.work_date}</TableCell>
                        <TableCell>{c.source_day_type}</TableCell>
                        <TableCell>{c.hours_worked}h</TableCell>
                        <TableCell>
                          {c.days_credited}d (Avail: {c.days_available}d)
                        </TableCell>
                        <TableCell>{c.expiry_date}</TableCell>
                        <TableCell>
                          <Chip size="small" label={c.status} />
                        </TableCell>
                        <TableCell align="right">
                          {c.status === 'PENDING' && (
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Button size="small" variant="contained" color="success" onClick={() => handleApproveCompOff(c.id)}>
                                Approve
                              </Button>
                              <Button size="small" variant="outlined" color="error" onClick={() => handleRejectCompOff(c.id)}>
                                Reject
                              </Button>
                            </Stack>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper sx={CARD_SX}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>
              60-Day Grant-Specific Comp-Off Expiry Sweep
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <TextField
                size="small"
                type="date"
                label="As-Of Date"
                value={expiryAsOfDate}
                onChange={(e) => setExpiryAsOfDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button variant="outlined" onClick={() => handleSweepExpiry(true)}>
                Preview Expired Grants (Dry-Run)
              </Button>
              <Button variant="contained" color="warning" onClick={() => handleSweepExpiry(false)}>
                Execute Expiry Sweep
              </Button>
            </Stack>
            {expiryPreview && (
              <Alert severity="info" sx={{ mt: 2 }}>
                As of {expiryPreview.as_of_date}: {expiryPreview.total_expired_days} day(s) across{' '}
                {expiryPreview.grants_evaluated ?? expiryPreview.grants_expired_count} grant(s).
              </Alert>
            )}
          </Paper>
        </Stack>
      )}

      {/* SUB-TAB 2: Year-End Preview & Rollover */}
      {subTab === 2 && (
        <Paper sx={CARD_SX}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            Year-End Carry-Forward, Lapse & Encashment Preview
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 2.5 }}>
            Generate a zero-write simulation preview of closing entitlements, carry-forward caps, lapsed balances, and
            DEC-001 non-payroll encashment estimates.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <TextField
              size="small"
              type="number"
              label="Closing Cycle Year"
              value={cycleYear}
              onChange={(e) => setCycleYear(Number(e.target.value))}
            />
            <Button variant="outlined" onClick={handlePreviewYearEnd} disabled={loadingYearEnd}>
              Run Read-Only Year-End Preview
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleExecuteYearEnd}
              disabled={loadingYearEnd || !b4Config?.is_year_end_processing_active}
            >
              Execute Policy Rollover Batch
            </Button>
          </Stack>

          {yearEndPreview && (
            <Box sx={{ mb: 3 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Cycle {yearEndPreview.cycle_year} Preview — Employees Evaluated:{' '}
                <strong>{yearEndPreview.employees_evaluated}</strong> | Total Carry-Forward:{' '}
                <strong>{yearEndPreview.total_carried_forward_days}d</strong> | Total Lapsed:{' '}
                <strong>{yearEndPreview.total_lapsed_days}d</strong> | Preview Encashable (Non-Payroll):{' '}
                <strong>{yearEndPreview.total_preview_encashable_days}d</strong>
              </Alert>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell>Leave Type</TableCell>
                      <TableCell align="right">Closing Avail</TableCell>
                      <TableCell align="right">CF Cap</TableCell>
                      <TableCell align="right">Carried Forward</TableCell>
                      <TableCell align="right">Lapsed</TableCell>
                      <TableCell align="right">Preview Encashable</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(yearEndPreview.items || []).map((item, idx) => (
                      <TableRow key={`${item.employee_id}-${item.leave_type_id}-${idx}`}>
                        <TableCell>{item.employee_name}</TableCell>
                        <TableCell>{item.leave_type_code}</TableCell>
                        <TableCell align="right">{item.closing_available}</TableCell>
                        <TableCell align="right">{item.carry_forward_cap}</TableCell>
                        <TableCell align="right">{item.carried_forward_days}</TableCell>
                        <TableCell align="right">{item.lapsed_days}</TableCell>
                        <TableCell align="right">{item.preview_encashable_days}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {yearEndBatches.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                Historical Year-End Batches
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Cycle Year</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Employees</TableCell>
                      <TableCell>Carried Forward</TableCell>
                      <TableCell>Lapsed</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {yearEndBatches.map((b) => (
                      <TableRow key={b.id}>
                        <TableCell>{b.cycle_year}</TableCell>
                        <TableCell>{b.status}</TableCell>
                        <TableCell>{b.employees_evaluated}</TableCell>
                        <TableCell>{b.total_carried_forward_days}d</TableCell>
                        <TableCell>{b.total_lapsed_days}d</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Paper>
      )}

      {/* Resolve Worked-on-Leave Dialog */}
      <Dialog
        open={resolveDialog.open}
        onClose={() => setResolveDialog((prev) => ({ ...prev, open: false }))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Resolve Worked-on-Leave Review</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="wol-decision-label">Decision</InputLabel>
              <Select
                labelId="wol-decision-label"
                label="Decision"
                value={resolveDialog.decision}
                onChange={(e) => setResolveDialog({ ...resolveDialog, decision: e.target.value })}
              >
                <MenuItem value="CONFIRMED_WORKED">Confirmed Worked (Credit Leave)</MenuItem>
                <MenuItem value="INCIDENTAL_PUNCH">Incidental Punch (Keep Leave Untouched)</MenuItem>
                <MenuItem value="NO_CHANGE">No Balance Change</MenuItem>
              </Select>
            </FormControl>

            {resolveDialog.decision === 'CONFIRMED_WORKED' && (
              <FormControl fullWidth size="small">
                <InputLabel id="wol-credit-label">Credit Days</InputLabel>
                <Select
                  labelId="wol-credit-label"
                  label="Credit Days"
                  value={resolveDialog.credit_days}
                  onChange={(e) => setResolveDialog({ ...resolveDialog, credit_days: e.target.value })}
                >
                  <MenuItem value="1.00">1.00 Full Day</MenuItem>
                  <MenuItem value="0.50">0.50 Half Day</MenuItem>
                </Select>
              </FormControl>
            )}

            <TextField
              fullWidth
              size="small"
              multiline
              rows={3}
              label="Mandatory Audit Reason"
              value={resolveDialog.reason}
              onChange={(e) => setResolveDialog({ ...resolveDialog, reason: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialog((prev) => ({ ...prev, open: false }))}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmResolve}>
            Confirm Resolution
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

